<template>
  <article
    :class="[
      'v2-benefit-card',
      {
        'v2-benefit-card--locked': locked,
        'v2-benefit-card--api': isApi,
        'v2-benefit-card--direct': !isApi,
      }
    ]"
  >
    <div class="v2-benefit-card__media" aria-hidden="true">
      <img
        v-if="hasRemoteIcon"
        :src="icon"
        :alt="name"
        class="v2-benefit-card__image"
        @error="onIconError"
      />
      <img
        v-else
        :src="placeholderImage"
        alt=""
        class="v2-benefit-card__image"
      />
    </div>

    <div class="v2-benefit-card__body">
      <div class="v2-benefit-card__head">
        <h3 class="v2-headline v2-headline--display-sm v2-benefit-card__title">
          {{ name }}
        </h3>
        <span v-if="isApi" class="v2-badge v2-badge--info v2-benefit-card__type-badge">
          API
        </span>
      </div>
      <p v-if="description" class="v2-body v2-body--sm v2-body--muted v2-benefit-card__description">
        {{ description }}
      </p>

      <p v-if="expirationLabel" class="v2-caption v2-benefit-card__expiry">
        <v-icon size="14" class="v2-benefit-card__expiry-icon">mdi-calendar-clock</v-icon>
        <span>Vigente hasta el {{ expirationLabel }}</span>
      </p>
    </div>

    <footer class="v2-benefit-card__footer">
      <BaseButton
        v-if="hasAction"
        :variant="isApi ? 'secondary' : 'amber'"
        size="sm"
        block
        :icon="isApi ? 'mdi-shield-link-variant-outline' : 'mdi-open-in-new'"
        :loading="loading"
        :disabled="locked"
        @click="onAction"
      >
        {{ actionLabel }}
      </BaseButton>
      <span v-else class="v2-benefit-card__no-action">
        <v-icon size="14" class="v2-benefit-card__no-action-icon">mdi-information-outline</v-icon>
        Sin enlace disponible
      </span>
    </footer>
  </article>
</template>

<script>
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import benefitPlaceholder from "@/assets/images/benefits/benefits-placeholder.png";
import {
  getBenefitName,
  getBenefitDescription,
  getBenefitIcon,
  getBenefitUrl,
  getBenefitType,
  formatBenefitExpiration,
  isApiBenefit,
  isValidImageUrl,
} from "./benefitsHelpers";

export default {
  name: "BenefitCard",
  components: { BaseButton },
  props: {
    benefit: { type: Object, required: true },
    locked: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      imageFailed: false,
    };
  },
  watch: {
    icon() {
      this.imageFailed = false;
    },
    benefit() {
      this.imageFailed = false;
    },
  },
  computed: {
    name() {
      return getBenefitName(this.benefit);
    },
    description() {
      return getBenefitDescription(this.benefit);
    },
    icon() {
      return getBenefitIcon(this.benefit);
    },
    url() {
      return getBenefitUrl(this.benefit);
    },
    isApi() {
      return isApiBenefit(this.benefit);
    },
    hasRemoteIcon() {
      return !this.imageFailed && isValidImageUrl(this.icon);
    },
    placeholderImage() {
      return benefitPlaceholder;
    },
    hasAction() {
      if (this.isApi) return true;
      return Boolean(this.url);
    },
    actionLabel() {
      return this.isApi ? "Obtener beneficio" : "Visitar sitio";
    },
    expirationLabel() {
      return formatBenefitExpiration(this.benefit);
    },
  },
  methods: {
    onIconError() {
      this.imageFailed = true;
    },
    onAction() {
      if (this.locked) return;
      this.$emit("action", { benefit: this.benefit });
    },
  },
};
</script>

<style scoped>
.v2-benefit-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
  height: 100%;
  transition: box-shadow var(--transition-base), transform var(--transition-base);
}

.v2-benefit-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.v2-benefit-card--locked {
  opacity: 0.55;
  filter: grayscale(0.3);
  pointer-events: none;
}

.v2-benefit-card__media {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: var(--radius-lg);
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
  flex-shrink: 0;
  overflow: hidden;
}

.v2-benefit-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.v2-benefit-card__icon-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.v2-benefit-card__initials {
  font-family: var(--font-headline);
  font-size: var(--fs-display-sm);
  font-weight: var(--fw-bold);
  color: var(--color-primary);
  line-height: 1;
}

.v2-benefit-card__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.v2-benefit-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-2);
}

.v2-benefit-card__title {
  margin: 0;
  color: var(--color-text);
  word-break: break-word;
}

.v2-benefit-card__type-badge {
  flex-shrink: 0;
}

.v2-benefit-card__description {
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.v2-benefit-card__expiry {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  color: var(--color-text-muted);
}

.v2-benefit-card__expiry-icon {
  color: var(--color-amber);
}

.v2-benefit-card__footer {
  margin-top: auto;
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-border);
}

.v2-benefit-card__no-action {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--fs-label-sm);
  color: var(--color-text-subtle);
}

.v2-benefit-card__no-action-icon {
  color: var(--color-text-subtle);
}
</style>
